import './index.css'

import React, { CSSProperties, ReactNode, useEffect } from 'react'
import { useDragLayer } from 'react-dnd'

import {
  getAllSelectedItemsCount,
  getSelectedItemsForPreview,
} from '@disco/state-management/selectors/selection'

import type { ShortlistTrackItem } from 'types/shortlist'

import ItemsDragPreview from 'components/common/drag/items-drag-preview'

import useDragReleased from 'hooks/use-drag-released'
import { useSelector } from 'hooks/use-redux'

type DragItem = Partial<ShortlistTrackItem> & {
  isPartOfMultipleSelection?: boolean
  preview?: ReactNode
  width?: number
  height?: number
  [key: string]: any
}

type XYCoord = { x: number; y: number }

function getItemStyles(
  initialOffset: XYCoord | null,
  currentOffset: XYCoord | null,
  clientOffset: XYCoord | null,
): CSSProperties {
  if (!initialOffset || !currentOffset) {
    return { display: 'none' }
  }
  const offset = clientOffset || currentOffset
  return { transform: `translate(${offset.x}px, ${offset.y}px)` }
}

export default function CustomDragLayer() {
  const selectedItemsCount = useSelector(getAllSelectedItemsCount)
  const previewItems = useSelector(getSelectedItemsForPreview)

  const {
    item,
    itemType,
    initialOffset,
    currentOffset,
    clientOffset,
    isDragging,
  } = useDragLayer(monitor => ({
    item: monitor.getItem() as DragItem | null,
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    clientOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }))

  // react-dnd can be slow to end a drag -- or never end it, when the source
  // row was unmounted mid-drag -- so the preview follows the pointer release
  // rather than the dragging state, which would keep it floating for seconds.
  const isReleased = useDragReleased(isDragging)
  const isPreviewVisible = isDragging && !isReleased

  useEffect(() => {
    if (document?.body) {
      document.body.style.cursor = isPreviewVisible ? 'grabbing' : ''
    }
  }, [isPreviewVisible])

  if (!isPreviewVisible) return null

  let dragPreview: ReactNode
  if (item?.isPartOfMultipleSelection) {
    dragPreview = (
      <ItemsDragPreview
        type={itemType}
        item={item}
        selectedItemsCount={selectedItemsCount}
        previewItems={previewItems}
      />
    )
  } else if (item?.preview) {
    dragPreview = item.preview
  }

  if (item?.width) {
    dragPreview = (
      <div
        className="Draggable-preview"
        style={{ width: item?.preview ? 'auto' : item?.width }}
      >
        {dragPreview}
      </div>
    )
  }

  return (
    <div className="CustomDragLayer">
      <div style={getItemStyles(initialOffset, currentOffset, clientOffset)}>
        {dragPreview}
      </div>
    </div>
  )
}
